import React, { useCallback, useEffect, useState } from "react";
import { coachApi } from "@/api";
import type { CoachConversation } from "@/lib/types";
import DirectChat from "./DirectChat";
import { useUI } from "@/lib/theme";

export default function CoachInbox() {
  const { t } = useUI();
  const [conversations, setConversations] = useState<CoachConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<CoachConversation | null>(null);

  const load = useCallback(() => {
    coachApi.conversations().then((r) => {
      setConversations(r.conversations);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, 30_000);
    return () => clearInterval(id);
  }, [load]);

  if (active) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-4">
        <button
          onClick={() => { setActive(null); load(); }}
          className="mb-4 flex items-center gap-1 rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-muted transition hover:text-fg"
        >
          ← {t("nav_red_messages")}
        </button>
        <div className="flex items-center gap-3 mb-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-white">
            {active.profilePic
              ? <img src={active.profilePic} alt={active.name} className="h-full w-full rounded-full object-cover" />
              : active.name.slice(0, 1).toUpperCase()}
          </span>
          <span className="font-semibold text-fg">{active.name}</span>
        </div>
        <DirectChat
          viewerRole="coach"
          fetchMessages={() => coachApi.messages(active.athleteId)}
          sendMessage={(body) => coachApi.sendMessage(active.athleteId, body)}
          peer={{ name: active.name }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-4">
      <h2 className="mb-4 text-lg font-bold text-fg">{t("nav_red_messages")}</h2>
      <div className="rounded-2xl border border-line bg-panel shadow-sm">
      {loading && (
        <div className="space-y-1 p-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-panel2" />)}
        </div>
      )}
      {!loading && conversations.length === 0 && (
        <p className="px-4 py-6 text-sm text-muted">{t("coach_roster_empty")}</p>
      )}
      {conversations.map((conv, idx) => (
        <button
          key={conv.athleteId}
          onClick={() => setActive(conv)}
          className={`flex w-full items-center gap-3 px-4 py-3 transition hover:bg-panel2/60 ${idx < conversations.length - 1 ? "border-b border-line" : ""}`}
        >
          <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-white">
            {conv.profilePic
              ? <img src={conv.profilePic} alt={conv.name} className="h-full w-full rounded-full object-cover" />
              : conv.name.slice(0, 1).toUpperCase()}
            {conv.unread > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[9px] font-bold text-white ring-2 ring-panel">
                {conv.unread > 9 ? "9+" : conv.unread}
              </span>
            )}
          </span>
          <div className="flex min-w-0 flex-1 flex-col text-left">
            <span className={`truncate text-sm font-semibold ${conv.unread > 0 ? "text-fg" : "text-fg/80"}`}>
              {conv.name}
            </span>
            <span className="truncate text-xs text-muted">
              {conv.lastMessage ?? "—"}
            </span>
          </div>
          {conv.lastAt && (
            <span className="shrink-0 text-[11px] text-muted">
              {new Date(conv.lastAt).toLocaleDateString()}
            </span>
          )}
        </button>
      ))}
      </div>
    </div>
  );
}
